/* eslint-disable @typescript-eslint/no-empty-function */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounceTime, fromEvent, map, Subscription } from 'rxjs';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssPopoverComponent } from '../popover/popover.component';
import { UmssTooltipDirective } from '../../directives/tooltip.directive';

@Component({
  selector: 'umss-textarea',
  standalone: true,
  imports: [
    CommonModule,
    UmssIconComponent,
    UmssPopoverComponent,
    UmssTooltipDirective
  ],
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssTextareaComponent),
      multi: true
    }
  ]
})
export class UmssTextareaComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';
  @ViewChild('textarea') textareaRef?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('richTextEditor')
  set richTextEditor(ref: ElementRef<HTMLElement> | undefined) {
    this.richTextEditorRef = ref;
    this.syncRichTextEditor();
  }
  @ViewChild('linkInput') linkInputRef?: ElementRef<HTMLInputElement>;

  readonly inputId = `umss-textarea-${Math.random().toString(36).slice(2)}`;

  constructor() {}

  ngOnDestroy(): void {
    this.scrollSub?.unsubscribe();
  }

  /** Content props */
  @Input() label?: string;
  @Input() placeholder: string = '';
  @Input() helperText?: string;
  @Input() errorMessage?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() rows: number = 4;
  @Input() maxLength?: number;
  @Input() resize: 'none' | 'vertical' | 'horizontal' | 'both' = 'vertical';
  @Input() richText: boolean = false;

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() error: boolean = false;

  /** Value prop */
  private _value: string = '';
  private richTextEditorRef?: ElementRef<HTMLElement>;
  @Input() set value(val: string) {
    this._value = val;
    this.syncRichTextEditor();
  }
  get value(): string {
    return this._value;
  }

  /** Event outputs */
  @Output() valueChange = new EventEmitter<string>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();
  @Output() onInput = new EventEmitter<string>();
  @Output() onScroll = new EventEmitter<number>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};
  private scrollSub?: Subscription;
  protected linkPopoverOpen = false;
  protected linkUrl = '';
  protected canAddLink = false;
  protected linkPopoverMode: 'create' | 'view' | 'edit' = 'create';
  protected activeBold = false;
  protected activeItalic = false;
  protected activeUnderline = false;
  protected activeOrderedList = false;
  protected activeUnorderedList = false;
  protected activeLink = false;
  private linkSelection: Range | null = null;
  private selectedLink: HTMLAnchorElement | null = null;

  ngAfterViewInit() {
    this.subscribeToScroll();
  }

  writeValue(value: string): void {
    this._value = value || '';
    this.syncRichTextEditor();
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Event handlers */
  onTextareaInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    let value = target.value;

    // Manual maxLength validation
    if (this.maxLength && value.length > this.maxLength) {
      value = value.substring(0, this.maxLength);
      target.value = value;
    }

    this.emitValue(value);
  }

  onRichTextInput(editor: HTMLElement): void {
    const text = editor.textContent ?? '';
    if (this.maxLength && text.length > this.maxLength) {
      editor.innerHTML = this._value;
      return;
    }

    this.emitValue(text.trim() ? editor.innerHTML : '');
  }

  formatRichText(
    event: MouseEvent,
    command:
      | 'bold'
      | 'italic'
      | 'underline'
      | 'insertOrderedList'
      | 'insertUnorderedList',
    editor: HTMLElement
  ): void {
    event.preventDefault();
    if (this.disabled || this.readonly) return;

    editor.focus();
    if (command === 'insertOrderedList' || command === 'insertUnorderedList') {
      this.ensureCurrentParagraph(editor);
    }
    document.execCommand(command);
    this.onRichTextInput(editor);
    this.updateFormattingState(editor);
  }

  onRichTextKeydown(event: KeyboardEvent, editor: HTMLElement): void {
    if (event.key === 'Enter') {
      this.ensureCurrentParagraph(editor);
      return;
    }

    if (event.key !== 'Backspace' && event.key !== 'Delete') return;

    const listItem = this.getCurrentListItem(editor);
    if (!listItem || listItem.textContent?.trim()) return;

    event.preventDefault();
    document.execCommand('outdent');
    this.onRichTextInput(editor);
  }

  openLinkPopover(event: MouseEvent, editor: HTMLElement): void {
    event.preventDefault();
    this.updateLinkAvailability(editor);
    if (this.disabled || this.readonly || !this.canAddLink) return;

    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    this.linkSelection = selection.getRangeAt(0).cloneRange();
    this.selectedLink = null;
    this.linkUrl = '';
    this.linkPopoverMode = 'create';
    this.linkPopoverOpen = true;
    setTimeout(() => this.linkInputRef?.nativeElement.focus());
  }

  applyRichTextLink(event: Event, editor: HTMLElement): void {
    event.preventDefault();
    if (!this.commitRichTextLink(editor)) return;
    this.closeLinkPopover();
  }

  onLinkPopoverClosed(editor: HTMLElement): void {
    if (this.linkPopoverMode !== 'view') {
      this.commitRichTextLink(editor);
    }
    this.closeLinkPopover();
  }

  onRichTextClick(event: MouseEvent, editor: HTMLElement): void {
    const target = event.target;
    const link = target instanceof Element ? target.closest('a') : null;
    if (!(link instanceof HTMLAnchorElement) || !editor.contains(link)) return;

    event.preventDefault();
    event.stopPropagation();
    this.selectedLink = link;
    this.linkUrl = link.getAttribute('href') ?? '';
    this.linkSelection = document.createRange();
    this.linkSelection.selectNodeContents(link);
    this.linkPopoverMode = 'view';
    this.linkPopoverOpen = true;
    this.updateFormattingState(editor);
  }

  editSelectedLink(): void {
    if (!this.selectedLink) return;

    this.linkPopoverMode = 'edit';
    setTimeout(() => {
      this.linkInputRef?.nativeElement.focus();
      this.linkInputRef?.nativeElement.select();
    });
  }

  deleteSelectedLink(editor: HTMLElement): void {
    if (!this.selectedLink) return;

    this.selectedLink.replaceWith(...Array.from(this.selectedLink.childNodes));
    editor.normalize();
    this.onRichTextInput(editor);
    this.closeLinkPopover();
    editor.focus();
  }

  onLinkUrlInput(event: Event): void {
    this.linkUrl = (event.target as HTMLInputElement).value;
  }

  closeLinkPopover(): void {
    this.linkPopoverOpen = false;
    this.linkUrl = '';
    this.linkSelection = null;
    this.selectedLink = null;
    this.linkPopoverMode = 'create';
    this.updateLinkAvailability(this.richTextEditorRef?.nativeElement);
  }

  updateLinkAvailability(editor?: HTMLElement): void {
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount || selection.isCollapsed) {
      this.canAddLink = false;
      if (editor) this.updateFormattingState(editor);
      return;
    }

    const range = selection.getRangeAt(0);
    this.canAddLink = editor.contains(range.commonAncestorContainer);
    this.updateFormattingState(editor);
  }

  onRichTextPaste(event: ClipboardEvent, editor: HTMLElement): void {
    event.preventDefault();
    document.execCommand(
      'insertText',
      false,
      event.clipboardData?.getData('text/plain') ?? ''
    );
    this.onRichTextInput(editor);
  }

  onTextareaFocus(): void {
    if (this.disabled) return;
    this.onFocus.emit();
  }

  onTextareaBlur(): void {
    if (this.disabled) return;
    this._onTouched();
    this.onBlur.emit();
  }

  /** Computed properties */
  get hasValue(): boolean {
    return !!(this._value && this._value.length > 0);
  }

  get characterCount(): string {
    if (!this.maxLength) return '';
    if (!this.richText) return `${this._value.length}/${this.maxLength}`;

    const documentFragment = new DOMParser().parseFromString(
      this._value,
      'text/html'
    );
    return `${documentFragment.body.textContent?.length ?? 0}/${this.maxLength}`;
  }

  private onScrollEvent(textarea: HTMLElement): number {
    const scrollTop = textarea.scrollTop;
    const scrollHeight = textarea.scrollHeight - textarea.clientHeight;

    return Math.round((scrollTop / scrollHeight) * 100);
  }

  private subscribeToScroll(): void {
    const textarea = this.textareaRef?.nativeElement;
    const richTextEditor = this.richTextEditorRef?.nativeElement;
    const scrollElement = textarea ?? richTextEditor;
    if (!scrollElement) return;

    this.scrollSub = fromEvent(scrollElement, 'scroll')
      .pipe(
        debounceTime(100),
        map(() => this.onScrollEvent(scrollElement))
      )
      .subscribe((percent) => {
        this.onScroll.emit(percent);
      });
  }

  private emitValue(value: string): void {
    this._value = value;
    this._onChange(value);
    this.valueChange.emit(value);
    this.onInput.emit(value);
  }

  private ensureCurrentParagraph(editor: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    let currentNode: Node | null = selection.getRangeAt(0).startContainer;
    while (currentNode && currentNode !== editor) {
      if (
        currentNode instanceof HTMLElement &&
        ['DIV', 'P', 'LI', 'BLOCKQUOTE'].includes(currentNode.tagName)
      ) {
        return;
      }
      currentNode = currentNode.parentNode;
    }

    document.execCommand('formatBlock', false, 'div');
  }

  private getCurrentListItem(editor: HTMLElement): HTMLLIElement | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return null;

    let currentNode: Node | null = selection.getRangeAt(0).startContainer;
    while (currentNode && currentNode !== editor) {
      if (currentNode instanceof HTMLLIElement) return currentNode;
      currentNode = currentNode.parentNode;
    }

    return null;
  }

  private updateFormattingState(editor: HTMLElement): void {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const isInEditor = Boolean(
      range && editor.contains(range.commonAncestorContainer)
    );

    this.activeBold = isInEditor && document.queryCommandState('bold');
    this.activeItalic = isInEditor && document.queryCommandState('italic');
    this.activeUnderline =
      isInEditor && document.queryCommandState('underline');
    this.activeOrderedList =
      isInEditor && document.queryCommandState('insertOrderedList');
    this.activeUnorderedList =
      isInEditor && document.queryCommandState('insertUnorderedList');

    let currentNode: Node | null = range?.startContainer ?? null;
    this.activeLink = false;
    while (currentNode && currentNode !== editor) {
      if (currentNode instanceof HTMLAnchorElement) {
        this.activeLink = true;
        break;
      }
      currentNode = currentNode.parentNode;
    }
  }

  private commitRichTextLink(editor: HTMLElement): boolean {
    const url = this.normalizeLinkUrl(this.linkUrl);
    if (!url || !this.linkSelection) return false;

    if (this.selectedLink) {
      this.selectedLink.setAttribute('href', url);
    } else {
      const anchor = document.createElement('a');
      anchor.setAttribute('href', url);
      anchor.append(this.linkSelection.extractContents());
      this.linkSelection.insertNode(anchor);
    }

    this.onRichTextInput(editor);
    return true;
  }

  private normalizeLinkUrl(value: string): string | null {
    const link = value.trim();
    if (!link || /\s/.test(link)) return null;

    if (/^mailto:/i.test(link)) {
      return /^mailto:[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(link) ? link : null;
    }

    const candidate = /^https?:\/\//i.test(link) ? link : `https://${link}`;
    try {
      const url = new URL(candidate);
      const hasValidHost =
        url.hostname === 'localhost' || url.hostname.includes('.');
      return ['http:', 'https:'].includes(url.protocol) && hasValidHost
        ? url.href
        : null;
    } catch {
      return null;
    }
  }

  private syncRichTextEditor(): void {
    const editor = this.richTextEditorRef?.nativeElement;
    if (editor && editor.innerHTML !== this._value) {
      editor.innerHTML = this._value;
    }
  }
}
